import { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { FaClipboard, FaKey } from 'react-icons/fa';
import { useMutation } from '@tanstack/react-query';
import { path } from 'lodash/fp';
import i18next from 'i18next';
import { useApiClient } from '../../hooks/useApiClient';
import { useErrorToast } from '../../hooks/useErrorToast';

type Step = 'idle' | 'confirming' | 'loading' | 'success';

type Props = {
  username: string;
};

const COPIED_INDICATOR_MS = 3000;

export function ResetUserPassword({ username }: Props) {
  const { apiClient, handleApi } = useApiClient();
  const { showError } = useErrorToast();

  const [step, setStep] = useState<Step>('idle');
  const [password, setPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isClipboardPresent = Boolean(path('clipboard.writeText', navigator));

  const { mutate } = useMutation({
    mutationFn: () => handleApi(apiClient.userController.resetPassword(username)),
    onSuccess: (data) => {
      setPassword(data.password);
      setStep('success');
    },
    onError: (err) => {
      showError(err, i18next.t('error_resetting_password'));
      setStep(password ? 'success' : 'idle');
    },
  });

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_INDICATOR_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const closeModal = () => {
    setStep('idle');
    setPassword(null);
    setCopied(false);
  };

  const copyPassword = async () => {
    if (!isClipboardPresent || !password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch (error) {
      console.debug(error);
    }
  };

  return (
    <>
      <Button onClick={() => setStep('confirming')}>
        <FaKey style={{ marginRight: '8px' }} />
        {i18next.t('reset_password')}
      </Button>

      <Modal
        show={step !== 'idle'}
        onHide={closeModal}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title className="display-6">
            {step === 'success'
              ? i18next.t('reset_password_new_password_label')
              : i18next.t('confirm')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {step === 'confirming' && (
            <p>{i18next.t('reset_password_confirmation_message', { username })}</p>
          )}
          {step === 'loading' && (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>{i18next.t('reset_password_loading')}</span>
            </div>
          )}
          {step === 'success' && password && (
            <div className="d-flex align-items-center gap-3">
              <code style={{ fontSize: '1.5rem', userSelect: 'all' }}>{password}</code>
              {isClipboardPresent && (
                <FaClipboard
                  style={{ cursor: 'pointer', fontSize: '24px' }}
                  onClick={copyPassword}
                />
              )}
              <span
                style={{
                  visibility: copied ? 'visible' : 'hidden',
                  minWidth: '60px',
                }}
              >
                {i18next.t('password_copied')}
              </span>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {step === 'confirming' && (
            <>
              <Button variant="link" onClick={closeModal}>
                {i18next.t('cancel')}
              </Button>
              <Button
                onClick={() => {
                  setCopied(false);
                  setStep('loading');
                  mutate();
                }}
              >
                {i18next.t('confirm')}
              </Button>
            </>
          )}
          {step === 'loading' && (
            <>
              <Button variant="link" disabled>
                {i18next.t('cancel')}
              </Button>
              <Button disabled>
                <Spinner animation="border" size="sm" />
              </Button>
            </>
          )}
          {step === 'success' && (
            <>
              <Button variant="link" onClick={closeModal}>
                {i18next.t('close')}
              </Button>
              {/* <Button
								onClick={() => {
									setCopied(false);
									setStep('loading');
									mutate();
								}}
							>
								{i18next.t('regenerate_password')}
							</Button> */}
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
